import { isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import type { AttachmentInput } from 'sr-types/lib/resource/v1/graphql';
import type { Contact, Filter } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../../common/auth/UserContext';
import { autoCreateQuery, client } from '../../common/components/person/person';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { FORM_WIDGET_COMPONENTS } from '../../common/form/formConstants';
import { FormContainer } from '../../common/form/FormContainer';
import { FormContext } from '../../common/form/FormContext';
import FormItem from '../../common/form/FormItem';
import FormPanel from '../../common/form/FormPanel';
import FormWidget from '../../common/form/FormWidget';
import { useMutationWithContext } from '../../common/hooks/useMutationWithContext';
import I18n from '../../common/i18n/I18n';
import { formatItemForOrg } from '../../hiringManager/hireResourceUtils';
import OrganizationOptionRenderer from '../../organization/OrganizationOptionRenderer';
import { getPersonOption, quickAddPerson } from '../../person/helpers/QuickAddPerson';
import { personDirectoryFiltersReferenceAutocomplete } from '../../person/person';
import ResourceOrderApproverEditor from '../../production/resource/orders/editor/resourceOrderApprovalsStep/ResourceOrderApproverEditor';
import AssociateActivityOptionRenderer from '../../resource/AssociateActivityOptionRenderer';
import { getActivityOptions } from '../../resource/ResourceAssociatedActivityList';
import { OrganizationInviteFormData } from './inquiry/inquiry';
import InquiryDateRangeFormItem from './inquiry/InquiryDateRangeFormItem';
import InquiryProductionFormItems from './inquiry/InquiryProductionFormItems';
import InquiryResourceFormItems from './inquiry/InquiryResourceFormItems';

export default ({
    data,
    isRequestAvailsForUser = false,
    filters = undefined,
    setAddOrgModalState,
    setAddOrgPublicName,
    saveAttachmentTags,
    onDeleteAttachmentFromState,
    setIsInUploadMode
}: {
    data: OrganizationInviteFormData;
    isRequestAvailsForUser?: boolean;
    filters?: Filter;
    setAddOrgModalState?: React.Dispatch<React.SetStateAction<boolean>>;
    setAddOrgPublicName?: React.Dispatch<React.SetStateAction<string>>;
    saveAttachmentTags?: (inputs: AttachmentInput[]) => void;
    onDeleteAttachmentFromState?: (attachments: AttachmentInput[], input: AttachmentInput) => AttachmentInput[];
    setIsInUploadMode: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const { state } = useContext(FormContext);
    const { organizationAccountDetails } = useContext(UserContext);

    const [notifyContactToEdit, setNotifyContactToEdit] = useState(null);
    const [senderContactToEdit, setSenderContactToEdit] = useState(null);

    const [savePerson] = useMutationWithContext(autoCreateQuery, client);
    const showNotifyField = state?.organizations?.length === 1;

    const findContactWithMissingEmail = (contacts: Contact[]) => contacts.find((c) => !c.contactInfo?.email?.address);

    const hasCreateOption = (contacts) => contacts.some((c) => c.createOption);

    useEffect(() => {
        const notifyContacts = state?.notifyContacts ?? [];

        if (!showNotifyField || hasCreateOption(notifyContacts)) {
            setNotifyContactToEdit((prev) => (prev === null ? prev : null));
            return;
        }

        const contactWithMissingEmail = findContactWithMissingEmail(notifyContacts);

        setNotifyContactToEdit((prev) => (prev === contactWithMissingEmail ? prev : contactWithMissingEmail));
    }, [state?.notifyContacts, showNotifyField]);

    useEffect(() => {
        const senderContacts = state?.senderContacts ?? [];

        if (hasCreateOption(senderContacts)) {
            setSenderContactToEdit(null);
            return;
        }

        setSenderContactToEdit(findContactWithMissingEmail(senderContacts));
    }, [state?.senderContacts]);

    const onAddOption = (newOrgName) => {
        setAddOrgModalState(true);
        setAddOrgPublicName(newOrgName?.label);
    };

    return (
        state && (
            <FormPanel sx={{ height: '100%', overflow: 'auto' }}>
                <FormItem>
                    <FormWidget
                        name="organizations"
                        label={<I18n token="organization.invite.form.label.organization" />}
                        component="ReferenceAutocomplete"
                        entity={'Organization'}
                        data-testid={'organization'}
                        multiple
                        disabled={!isRequestAvailsForUser}
                        preload={false}
                        filter={filters}
                        OptionRenderer={OrganizationOptionRenderer}
                        getOption={(item) => {
                            return formatItemForOrg(item);
                        }}
                        notOnlyRef={isRequestAvailsForUser}
                        addOption={isRequestAvailsForUser}
                        onCreateHandle={(newOrgName) => onAddOption(newOrgName)}
                    />
                </FormItem>
                <FormItem hidden={!showNotifyField}>
                    <FormWidget
                        component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
                        entity={ENTITY_CONSTANTS.person}
                        name="notifyContacts"
                        filter={personDirectoryFiltersReferenceAutocomplete}
                        getOption={getPersonOption}
                        label={
                            <I18n
                                token={'form.title.notify.contact.at'}
                                values={{ name: state?.organizations?.[0]?.label }}
                            />
                        }
                        preload={false}
                        saveMutation={savePerson}
                        onCreateHandle={(newValue, saveMutation) => quickAddPerson(newValue, saveMutation, true)}
                        addOption
                        notOnlyRef
                        multiple
                        disabled={!!notifyContactToEdit && isRequestAvailsForUser}
                    />
                </FormItem>
                {isRequestAvailsForUser && (
                    <FormItem hidden={!notifyContactToEdit}>
                        <ResourceOrderApproverEditor
                            contact={notifyContactToEdit}
                            setContact={setNotifyContactToEdit}
                            fieldNameKey={'notifyContacts'}
                        />
                    </FormItem>
                )}
                <FormItem>
                    <FormWidget
                        component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
                        entity={ENTITY_CONSTANTS.person}
                        name="senderContacts"
                        filter={personDirectoryFiltersReferenceAutocomplete}
                        getOption={getPersonOption}
                        label={
                            <I18n
                                token={'form.title.notify.sender.contact.at'}
                                values={{ name: organizationAccountDetails?.organizationName }}
                            />
                        }
                        preload={false}
                        saveMutation={savePerson}
                        onCreateHandle={(newValue, saveMutation) => quickAddPerson(newValue, saveMutation, true)}
                        addOption
                        notOnlyRef
                        multiple
                        disabled={!isEmpty(senderContactToEdit)}
                    />
                </FormItem>
                {isRequestAvailsForUser && (
                    <FormItem hidden={isEmpty(senderContactToEdit)}>
                        <ResourceOrderApproverEditor
                            contact={senderContactToEdit}
                            setContact={setSenderContactToEdit}
                            fieldNameKey={'senderContacts'}
                        />
                    </FormItem>
                )}
                <InquiryResourceFormItems data={data} />
                <InquiryDateRangeFormItem dateRange={data?.dateRange} />
                <InquiryProductionFormItems
                    productionOptions={data?.productionOptions}
                    productionName={data?.production?.name}
                    isRequestAvailsForUser={isRequestAvailsForUser}
                />
                <FormContainer>
                    <FormItem>
                        <FormWidget
                            name="productionSummary"
                            component={'TextArea'}
                            label={<I18n token="organization.invite.form.label.production.summary" />}
                            minRows={3}
                            disabled={!isEmpty(data.productionSummary)}
                        />
                    </FormItem>
                </FormContainer>
                <FormContainer>
                    <FormItem>
                        <FormWidget
                            multiple
                            name="activities"
                            component="ReferenceAutocomplete"
                            entity="Activity"
                            label={<I18n token="organization.invite.form.label.production.schedule" />}
                            filter={{
                                expressions: [
                                    {
                                        field: 'production',
                                        value: {
                                            values: data?.production?.reference?.id
                                                ? [data.production.reference.id]
                                                : []
                                        }
                                    }
                                ]
                            }}
                            disableIfOptionUnavailable
                            getOption={getActivityOptions}
                            OptionRenderer={AssociateActivityOptionRenderer}
                        />
                    </FormItem>
                </FormContainer>
                <FormContainer>
                    <FormItem>
                        <FormWidget
                            name="personalizedMessage"
                            component={'TextArea'}
                            label={<I18n token="organization.invite.form.label.message" />}
                            minRows={3}
                        />
                    </FormItem>
                </FormContainer>
                <FormContainer>
                    <FormItem>
                        <FormWidget
                            component={FORM_WIDGET_COMPONENTS.attachments}
                            label={<I18n token="attachments.title" />}
                            name={'attachments'}
                            entity={ENTITY_CONSTANTS.shortlistLineitem}
                            onDeleteAttachments={onDeleteAttachmentFromState}
                            onSaveAttachments={(entityId, inputs) => saveAttachmentTags(inputs)}
                            setIsInUploadMode={setIsInUploadMode}
                        />
                    </FormItem>
                </FormContainer>
            </FormPanel>
        )
    );
};
